import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HttpError, type MicePremisesRequest } from '@voll-intelligence/domain';
import { queryKeys } from '@web/core/query';
import { miceRepository } from '@web/data/repositories';
import { t } from '@web/i18n';
import { useOrgContext, useSnackbar } from '@web/ui/core';

/** 503 = serviço de extração desligado; texto do servidor nunca vai para a tela. */
const extractErrorMessage = (err: unknown): string =>
  err instanceof HttpError && err.status === 503
    ? t('mice.premises.extractUnavailable')
    : t('mice.premises.extractFailed');

export function useMicePremises(projectId: string) {
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const extract = useMutation({
    mutationFn: (premisesText: string) =>
      miceRepository.extractPremises(orgId, projectId, premisesText),
    onError: (err) => {
      showSnackbar({ severity: 'error', message: extractErrorMessage(err) });
    },
    meta: { silent: true },
  });

  const save = useMutation({
    mutationFn: (body: MicePremisesRequest) => miceRepository.savePremises(orgId, projectId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.mice.project(orgId, projectId) });
      showSnackbar({ severity: 'success', message: t('mice.premises.saved') });
    },
    onError: (err) => {
      showSnackbar({
        severity: 'error',
        message: err instanceof Error ? err.message : t('mice.error.generic'),
      });
    },
    meta: { silent: true },
  });

  return { extract, save };
}
